// Values more than one record holds: when it starts, how it repeats (Models.swift: Start, RepeatRule), in the shapes
// Swift's Codable has always stored them.

import { storedDay } from "../days";
import type { MonthPosition, RepeatRule, Start, Weekday } from "../types";
import {
  DecodeFailure, enumCase, FieldReader, readArray, readDay, readEnum, readInt, readStruct,
  type DecodeContext, type Fields, type ValueReader,
} from "./values";

// MARK: - Start

const START_CASES = ["inbox", "anytime", "someday", "on"] as const;

/**
 * `Start` as Swift stores it (Days.swift): `{"inbox": {}}`, `{"anytime": {}}`, `{"someday": {}}` or
 * `{"on": {"_0": day}}`. Exactly one known case; the empty map of a case without a value isn't looked at.
 */
export const readStart: ValueReader<Start> = (value, path, context) => {
  const found = enumCase(value, START_CASES);
  if (!found) throw new DecodeFailure(`${path} has the wrong shape`);
  switch (found.name) {
    case "inbox": return { kind: "inbox" };
    case "anytime": return { kind: "anytime" };
    case "someday": return { kind: "someday" };
    case "on": {
      const payload = found.payload;
      if (typeof payload !== "object" || payload === null || Array.isArray(payload)) throw new DecodeFailure(`${path} has the wrong shape`);
      const stored = (payload as Fields)._0;
      if (stored === undefined || stored === null) throw new DecodeFailure(`${path} has the wrong shape`);
      return { kind: "on", day: readDay(stored, `${path}.on._0`, context) };
    }
  }
};

export function encodeStart(start: Start): Fields {
  switch (start.kind) {
    case "inbox": return { inbox: {} };
    case "anytime": return { anytime: {} };
    case "someday": return { someday: {} };
    case "on": return { on: { _0: storedDay(start.day) } };
  }
}

// MARK: - RepeatRule

const UNITS = ["day", "week", "month", "year"] as const;

const readMonthPosition: ValueReader<MonthPosition> = readStruct((fields) => {
  const position: MonthPosition = { ordinal: fields.int("ordinal") };
  const weekday = fields.optionalInt("weekday");
  if (weekday !== undefined) position.weekday = weekday as Weekday;
  return position;
});

/** A set of weekdays (`Set<Int>`): stored as an array, each value once. */
const readWeekdays: ValueReader<Weekday[]> = (value, path, context) => {
  if (!Array.isArray(value)) throw new DecodeFailure(`${path} has the wrong shape`);
  const days = value.map((item, index) => readInt(item, `${path}[${index}]`, context) as Weekday);
  return [...new Set(days)];
};

/** `RepeatRule` (Models.swift): interval, unit and afterCompletion always; the rest when set. */
export const readRepeatRule: ValueReader<RepeatRule> = readStruct((fields) => {
  const rule: RepeatRule = {
    interval: fields.int("interval"),
    unit: fields.required("unit", readEnum(UNITS)),
    afterCompletion: fields.bool("afterCompletion"),
  };
  const weekdays = fields.optional("weekdays", readWeekdays);
  if (weekdays !== undefined) rule.weekdays = weekdays;
  const monthPosition = fields.optional("monthPosition", readMonthPosition);
  if (monthPosition !== undefined) rule.monthPosition = monthPosition;
  // Days of the month (docs/67): optional, written only when chosen; older versions ignore them.
  const monthDays = fields.optional("monthDays", readArray(readInt));
  if (monthDays !== undefined) rule.monthDays = monthDays;
  const endsOn = fields.optionalDay("endsOn");
  if (endsOn !== undefined) rule.endsOn = endsOn;
  return rule;
});

export function encodeRepeatRule(rule: RepeatRule): Fields {
  const fields: Fields = { interval: rule.interval, unit: rule.unit, afterCompletion: rule.afterCompletion };
  if (rule.weekdays !== undefined) fields.weekdays = [...rule.weekdays];
  if (rule.monthPosition !== undefined) {
    fields.monthPosition = rule.monthPosition.weekday === undefined
      ? { ordinal: rule.monthPosition.ordinal }
      : { ordinal: rule.monthPosition.ordinal, weekday: rule.monthPosition.weekday };
  }
  if (rule.monthDays !== undefined) fields.monthDays = [...rule.monthDays];
  if (rule.endsOn !== undefined) fields.endsOn = storedDay(rule.endsOn);
  return fields;
}

// MARK: - Helpers for the records

/** Reads a whole record with `read`, which sees the document's fields. */
export function readRecord<T>(data: Fields, context: DecodeContext, read: (fields: FieldReader) => T): T {
  return read(new FieldReader(data, context));
}
